import { Alert, AlertIcon, Button, Flex, Text } from "@chakra-ui/react";
import { useFlags } from "flagsmith/react";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import { useSchoolAdminPermissions } from "src/hooks/useSchoolAdminPermissions";
import { useSendMessage } from "src/hooks/useSendMessage";
import { getGuardians, isEligibleForMessaging } from "src/services/person";
import * as GQL from "src/types/graphql";
import { GET_FORM_MESSAGES } from "../../graphql/queries";
import { FormMessageCard } from "./FormMessageCard";
import { NoMessages } from "./NoMessages";
import { toFormMessageList } from "./helpers";

interface Props {
  form: GQL.GetFormViewById_form_by_pk;
  formTemplate: GQL.FormTemplateFragment;
}

export const FormMessages: React.FC<Props> = ({ form, formTemplate }) => {
  const { handleSendMessage } = useSendMessage(formTemplate.id);
  const flags = useFlags(["unverified-communications"]);
  const formParents = getGuardians(form.person);

  const isUnverifiedCommsEnabledForOrg =
    flags["unverified-communications"].enabled;

  const isEligibleForMessage = formParents.some((parent) =>
    isEligibleForMessaging(parent, isUnverifiedCommsEnabledForOrg)
  );
  const {
    messageAdhocReceiverFilter,
    messageFormStatusFilter,
    messageOfferStatusFilter,
    messageWaitlistStatusFilter,
  } = useSchoolAdminPermissions();
  const schoolAdmin = useSchoolAdmin();

  const { remoteData } = useRemoteDataQuery<
    GQL.GetFormMessages,
    GQL.GetFormMessagesVariables
  >(GET_FORM_MESSAGES, {
    variables: {
      form_id: form.id,
      message_receiver_filter: messageAdhocReceiverFilter(),
      message_form_status_filter: messageFormStatusFilter(),
      message_offer_status_filter: messageOfferStatusFilter(),
      message_waitlist_status_filter: messageWaitlistStatusFilter(),
      skip_content: schoolAdmin.isSchoolAdmin,
    },
    skip: !form.id,
    fetchPolicy: "no-cache",
  });

  return (
    <GQLRemoteDataView remoteData={remoteData}>
      {(data) => {
        const formattedFormMessages = toFormMessageList(data);

        if (formattedFormMessages.length === 0) {
          return <NoMessages form={form} formTemplate={formTemplate} />;
        }

        return (
          <Flex direction="column" gap={"1.5rem"}>
            <Flex justifyContent="space-between" alignItems="center" my={2}>
              <Text fontSize="sm" color="gray.600">
                All messages (latest to oldest)
              </Text>

              {isEligibleForMessage && (
                <WithUserPermissions permissions={["message:create"]}>
                  <Button
                    onClick={() => handleSendMessage([form.id])}
                    size="xs"
                  >
                    Send new message
                  </Button>
                </WithUserPermissions>
              )}
            </Flex>
            <Alert status="info" borderRadius={"6px"} p="1rem">
              <AlertIcon mr="0.75rem" />
              <Text>
                <Text as="span" fontWeight="700" display="inline">
                  Why you see variable text:
                </Text>{" "}
                Placeholders like {`{{student.name}}`} are filled in when the
                message is sent. We don't store the personalized version - so
                message history shows the original placeholder. Don't worry
                though, the right info was sent!
              </Text>
            </Alert>
            {formattedFormMessages.map((formMessage, index) => (
              <FormMessageCard key={index} formMessage={formMessage} />
            ))}
            <Text fontSize="sm" color="gray.600">
              End of message history
            </Text>
          </Flex>
        );
      }}
    </GQLRemoteDataView>
  );
};
